/**
 * The customer's phone of a contract, looked up in the contacts store
 * (contactsService, loaded once per day) with the row's own phone as fallback.
 * A contract not in memory (a closed one) is read from the store by number.
 * Renders a WhatsApp link (international number) or a dash.
 *
 *   <ContactPhone contractNo={row.contractNo} fallback={row.phone} />
 *
 * `useContacts()` re-renders the caller once the store is in memory; it is
 * safe to call on every page (no I/O once loaded).
 */
import React, { useEffect, useState } from 'react';
import { contactsService, phoneDigits, formatPhone, whatsappUrl } from '../services/contactsService';

export function useContacts() {
    const [ready, setReady] = useState(contactsService.isLoaded());
    useEffect(() => {
        let alive = true;
        if (!ready) contactsService.ensureLoaded().then(() => { if (alive) setReady(contactsService.isLoaded()); });
        return () => { alive = false; };
    }, [ready]);
    return ready;
}

/** Phone digits of a contract: the contacts store first, then the row's own value. */
export const contactPhoneOf = (contractNo, fallback) => {
    const row = contactsService.peek(contractNo);
    return (row && row.p) || phoneDigits(fallback);
};

/** A contract memory lacks (a closed one - memory holds the open contracts): asked from the store, the page's cells
 *  together (contactsService.want), and the cell shown again when it is in. */
function useStoreRow(contractNo, known) {
    const [, setRead] = useState(0);
    useEffect(() => {
        if (known || !contractNo) return undefined;
        const off = contactsService.onFetched(() => setRead((n) => n + 1));
        contactsService.want(contractNo);
        return off;
    }, [contractNo, known]);
}

export default function ContactPhone({ contractNo, fallback, className = '', empty = '—', stopPropagation = true }) {
    useContacts();
    const row = contactsService.peek(contractNo);
    useStoreRow(contractNo, !!row);
    const digits = (row && row.p) || phoneDigits(fallback);
    if (!digits) return <span className={`ds-cell-empty ${className}`}>{empty}</span>;
    return (
        <a
            href={whatsappUrl(digits)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (stopPropagation) e.stopPropagation(); }}
            className={`ds-cell-phone whitespace-nowrap hover:underline ${className}`}
            title={row && row.n ? `${row.n} · WhatsApp` : 'WhatsApp'}
        >
            {formatPhone(digits)}
        </a>
    );
}
